import store from '@/store'
import { apolloClient } from '@/vue-apollo'
import { loadStripe } from '@stripe/stripe-js'
import GetInvoices from '@/graphql/queries/GetInvoices'
import GetPaymentMethod from '@/graphql/mutations/GetPaymentMethod'
import AddPaymentMethod from '@/graphql/mutations/AddPaymentMethod'
import ResetBillingCycle from '@/graphql/mutations/ResetBillingCycle'
import CreateSubscription from '@/graphql/mutations/CreateSubscription'
import CancelSubscription from '@/graphql/mutations/CancelSubscription'
import HandlePaymentSuccess from '@/graphql/mutations/HandlePaymentSuccess'
import GetActiveSubscription from '@/graphql/mutations/GetActiveSubscription'
import AddUsersToSubscription from '@/graphql/mutations/AddUsersToSubscription'
import GetPayerUserSubscription from '@/graphql/queries/GetPayerUserSubscription'
import RemoveUserFromSubscription from '@/graphql/mutations/RemoveUserFromSubscription'
import ValidateUsersSubscriptions from '@/graphql/mutations/ValidateUsersSubscriptions'
import DowngradeSubscriptionQuantity from '@/graphql/mutations/DowngradeSubscriptionQuantity'
import { getSubscriptionAccessType } from '@/utils/payments'

export const payments = {
  namespaced: true,
  state: {
    subscriptionType: 'monthly',
    plan: null,
    users: [],
    totalPrice: 0,
    loadingCreateSubscription: false,
    loadingSubscriptionUsers: false,
    subscriptionUsers: null,
    loadingActiveSubscription: false,
    activeSubscription: null,
    loadingCancelSubscription: false,
    loadingAddUsersToSubscription: false,
    loadingRemoveUserFromSubscription: false,
    loadingDowngradeSubscriptionQuantity: false,
    paymentMethod: null,
    loadingPaymentMethod: false,
    invoices: [],
    loadingInvoices: false,
    loadingValidateUsersSubscriptions: false,
    isEnterpriseUser: false,
    isSubscriptionAdmin: false,
    subscriptionAccessType: 'NO_ACCESS'
  },
  mutations: {
    setPlan (state, payload) {
      state.plan = payload
    },
    setSubscriptionType (state, payload) {
      state.subscriptionType = payload
    },
    addNewUser (state, newUser) {
      const alreadyExists = state.users.findIndex(user => user.email === newUser.email) !== -1
      if (!alreadyExists) {
        state.users = [...state.users, newUser]
      }
    },
    removeUserByEmail (state, email) {
      state.users = state.users.filter(user => user.email !== email)
    },
    setTotalPrice (state, totalPrice) {
      state.totalPrice = totalPrice
    },
    setLoadingSubscriptionUsers (state, loadingSubscriptionUsers) {
      state.loadingSubscriptionUsers = loadingSubscriptionUsers
    },
    setLoadingCreateSubscription (state, loadingCreateSubscription) {
      state.loadingCreateSubscription = loadingCreateSubscription
    },
    setSubscriptionUsers (state, subscriptionUsers) {
      state.subscriptionUsers = subscriptionUsers
    },
    setLoadingActiveSubscription (state, loadingActiveSubscription) {
      state.loadingActiveSubscription = loadingActiveSubscription
    },
    setActiveSubscription (state, activeSubscription) {
      state.activeSubscription = activeSubscription
    },
    setLoadingCancelSubscription (state, loadingCancelSubscription) {
      state.loadingCancelSubscription = loadingCancelSubscription
    },
    setLoadingAddUsersToSubscription (state, loadingAddUsersToSubscription) {
      state.loadingAddUsersToSubscription = loadingAddUsersToSubscription
    },
    setLoadingRemoveUserFromSubscription (state, loadingRemoveUserFromSubscription) {
      state.loadingRemoveUserFromSubscription = loadingRemoveUserFromSubscription
    },
    setLoadingDowngradeSubscriptionQuantity (state, loadingDowngradeSubscriptionQuantity) {
      state.loadingDowngradeSubscriptionQuantity = loadingDowngradeSubscriptionQuantity
    },
    setLoadingPaymentMethod (state, loadingPaymentMethod) {
      state.loadingPaymentMethod = loadingPaymentMethod
    },
    setPaymentMethod (state, paymentMethod) {
      state.paymentMethod = paymentMethod
    },
    setLoadingInvoices (state, loadingInvoices) {
      state.loadingInvoices = loadingInvoices
    },
    setInvoices (state, invoices) {
      state.invoices = invoices
    },
    setLoadingValidateUsersSubscriptions (state, loadingValidateUsersSubscriptions) {
      state.loadingValidateUsersSubscriptions = loadingValidateUsersSubscriptions
    },
    setIsEnterpriseUser (state, isEnterpriseUser) {
      state.isEnterpriseUser = isEnterpriseUser
    },
    setIsSubscriptionAdmin (state, isSubscriptionAdmin) {
      state.isSubscriptionAdmin = isSubscriptionAdmin
    },
    setSubscriptionAccessType (state, subscriptionAccessType) {
      state.subscriptionAccessType = subscriptionAccessType
    }
  },
  actions: {
    async getPaymentMethod ({ commit, state, rootGetters, dispatch }) {
      try {
        commit('setLoadingPaymentMethod', true)
        const res = await apolloClient.query({
          query: GetPaymentMethod,
          fetchPolicy: 'no-cache'
        })
        commit('setPaymentMethod', res.data.getPaymentMethod)
        commit('setLoadingPaymentMethod', false)
        return res.data.getPaymentMethod
      } catch (e) {
        console.log(e)
        commit('setLoadingPaymentMethod', false)
        commit('snackbar/showMessage', {
          content: 'Error, try again!',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }
    },
    async addPaymentMethod ({ commit, state, rootGetters, dispatch }, { paymentMethodId, newCustomerInfo }) {
      try {
        const res = await apolloClient.mutate({
          mutation: AddPaymentMethod,
          variables: {
            paymentMethodId,
            newCustomerInfo
          },
          fetchPolicy: 'no-cache'
        })
        return res.data.addPaymentMethod
      } catch (e) {
        console.log(e)
        commit('snackbar/showMessage', {
          content: 'Error, try again!',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }
    },
    async createSubscription ({ commit, state, dispatch, rootState }, { subscriptionPriceId, users, stripe }) {
      try {
        commit('setLoadingCreateSubscription', true)
        const res = await apolloClient.mutate({
          mutation: CreateSubscription,
          variables: {
            subscriptionPriceId,
            users
          },
          fetchPolicy: 'no-cache'
        })

        const result = res.data.createSubscription
        const { success, message, clientSecret, subscriptionId, isFirstSubscription, eligibleForTrial } = result || {}

        if (!success) {
          return { success: false, message }
        }

        const handlePaymentSuccessParams = {
          users,
          subscriptionId,
          isFirstSubscription,
          eligibleForTrial
        }

        if (clientSecret) {
          // Existing clientSecret in response means that payment requires authentication via 3D Secure
          const stripeInstance = stripe || await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
          const confirmCardResponse = await stripeInstance.confirmCardPayment(clientSecret)

          if (confirmCardResponse.error) {
            if (isFirstSubscription && rootState.user?.user) {
              // Set stripeCustomerId to null in DB
              const { email } = rootState.user.user

              const variables = {
                email,
                stripeCustomerId: '',
                increaseLoginCounter: false
              }
              await dispatch('user/updateUser', variables, { root: true })
            }

            commit('snackbar/showMessage', {
              content: 'Authentication failed',
              timeout: 6000,
              color: 'red',
              location: 'center center'
            }, { root: true })
            commit('setLoadingCreateSubscription', false)

            console.log('CONFIRMATION_ERROR', confirmCardResponse.error)
            return {
              success: false,
              message: 'Authentication failed'
            }
          }
        }

        const handlePaymentSuccessResponse = await store.dispatch('payments/handlePaymentSuccess', handlePaymentSuccessParams)

        commit('setLoadingCreateSubscription', false)
        return handlePaymentSuccessResponse
      } catch (e) {
        console.log(e)
        commit('setLoadingCreateSubscription', false)
        commit('snackbar/showMessage', {
          content: 'Error, try again!',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
        return { success: false, message: 'Error, try again!' }
      }
    },
    async resetBillingCycle ({ commit }, { subscriptionId }) {
      try {
        const res = await apolloClient.mutate({
          mutation: ResetBillingCycle,
          variables: { subscriptionId },
          fetchPolicy: 'no-cache'
        })

        return res.data.resetBillingCycle
      } catch (e) {
        console.log(e)
        commit('snackbar/showMessage', {
          content: 'Error, try again!',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }
    },
    async handlePaymentSuccess ({ commit }, payload) {
      try {
        const res = await apolloClient.mutate({
          mutation: HandlePaymentSuccess,
          variables: { input: payload },
          fetchPolicy: 'no-cache'
        })

        return res.data.handlePaymentSuccess
      } catch (e) {
        console.log(e)
        commit('snackbar/showMessage', {
          content: 'Error, try again!',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }
    },
    async getPayerUserSubscription ({ commit, state, rootGetters, dispatch }) {
      commit('setLoadingSubscriptionUsers', true)
      try {
        const res = await apolloClient.query({
          query: GetPayerUserSubscription,
          fetchPolicy: 'no-cache'
        })

        commit('setSubscriptionUsers', res.data.getPayerUserSubscription || [])
        commit('setLoadingSubscriptionUsers', false)
        return res.data.getPayerUserSubscription || []
      } catch (e) {
        console.log(e)
        commit('setLoadingSubscriptionUsers', false)
        commit('snackbar/showMessage', {
          content: 'Error, try again!',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }
    },
    async getActiveSubscription ({ commit, state, rootGetters, dispatch }) {
      commit('setLoadingActiveSubscription', true)
      try {
        const res = await apolloClient.mutate({
          mutation: GetActiveSubscription,
          fetchPolicy: 'no-cache'
        })

        const activeSubscription = res.data.getActiveSubscription
        const isEnterpriseUser = !!activeSubscription && activeSubscription?.subscriptionDetails?.subscriptionId?.startsWith('ecus_')
        const isSubscriptionAdmin = !!store.state.user.user && activeSubscription && activeSubscription?.payerUserId === store.state.user.user.id
        const subscriptionAccessType = getSubscriptionAccessType(activeSubscription)

        commit('setActiveSubscription', activeSubscription)
        commit('setIsEnterpriseUser', isEnterpriseUser)
        commit('setIsSubscriptionAdmin', isSubscriptionAdmin)
        commit('setSubscriptionAccessType', subscriptionAccessType)
        commit('setLoadingActiveSubscription', false)

        if (subscriptionAccessType !== 'FULL') {
          commit('setWorkflowDisabled', true, { root: true })
        }

        return activeSubscription
      } catch (e) {
        console.log(e)
        commit('setLoadingActiveSubscription', false)
        commit('snackbar/showMessage', {
          content: 'Error, try again!',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }
    },
    async cancelSubscription ({ commit, state, rootGetters, dispatch }) {
      commit('setLoadingCancelSubscription', true)
      try {
        const res = await apolloClient.mutate({
          mutation: CancelSubscription,
          fetchPolicy: 'no-cache'
        })

        commit('setLoadingCancelSubscription', false)
        dispatch('getActiveSubscription')
        return res.data.cancelSubscription
      } catch (e) {
        console.log(e)
        commit('setLoadingCancelSubscription', false)
        commit('snackbar/showMessage', {
          content: 'Error, try again!',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }
    },
    async addUsersToSubscription ({ commit, state, rootGetters, dispatch }, { users }) {
      commit('setLoadingAddUsersToSubscription', true)
      try {
        const res = await apolloClient.mutate({
          mutation: AddUsersToSubscription,
          variables: { users },
          fetchPolicy: 'no-cache'
        })

        const result = res.data.addUsersToSubscription
        const { success, message, clientSecret, subscriptionId, newUsersExceedSeats, newQuantity } = result || {}

        if (!success) {
          return { success: false, message }
        }

        const handlePaymentSuccessParams = {
          users,
          subscriptionId,
          addingNewUsers: true,
          newUsersExceedSeats,
          newQuantity
        }

        if (clientSecret) {
          // Existing clientSecret in response means that payment requires authentication via 3D Secure
          const stripeInstance = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
          const confirmCardResponse = await stripeInstance.confirmCardPayment(clientSecret)

          if (confirmCardResponse.error) {
            commit('snackbar/showMessage', {
              content: 'Authentication failed',
              timeout: 6000,
              color: 'red',
              location: 'center center'
            }, { root: true })
            commit('setLoadingAddUsersToSubscription', false)

            console.log('CONFIRMATION_ERROR', confirmCardResponse.error)
            return {
              success: false,
              message: 'Authentication failed'
            }
          }
        }

        const handlePaymentSuccessResponse = await store.dispatch('payments/handlePaymentSuccess', handlePaymentSuccessParams)

        commit('setLoadingAddUsersToSubscription', false)
        if (handlePaymentSuccessResponse.success) {
          dispatch('getActiveSubscription')
          dispatch('getPayerUserSubscription')
        }
        return handlePaymentSuccessResponse
      } catch (e) {
        console.log(e)
        commit('setLoadingAddUsersToSubscription', false)
        commit('snackbar/showMessage', {
          content: 'Error, try again!',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
        return { success: false, message: 'Error, try again!' }
      }
    },
    async removeUserFromSubscription ({ commit, state, rootGetters, dispatch }, { email }) {
      commit('setLoadingRemoveUserFromSubscription', true)
      try {
        const res = await apolloClient.mutate({
          mutation: RemoveUserFromSubscription,
          variables: { email },
          fetchPolicy: 'no-cache'
        })

        commit('setLoadingRemoveUserFromSubscription', false)
        dispatch('getPayerUserSubscription')
        return res.data.removeUserFromSubscription
      } catch (e) {
        console.log(e)
        commit('setLoadingRemoveUserFromSubscription', false)
        commit('snackbar/showMessage', {
          content: 'Error, try again!',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }
    },
    async downgradeSubscriptionQuantity ({ commit, state, rootGetters, dispatch }) {
      commit('setLoadingDowngradeSubscriptionQuantity', true)
      try {
        const res = await apolloClient.mutate({
          mutation: DowngradeSubscriptionQuantity,
          fetchPolicy: 'no-cache'
        })

        commit('setLoadingDowngradeSubscriptionQuantity', false)
        dispatch('getActiveSubscription')
        return res.data.downgradeSubscriptionQuantity
      } catch (e) {
        console.log(e)
        commit('setLoadingDowngradeSubscriptionQuantity', false)
        commit('snackbar/showMessage', {
          content: 'Error, try again!',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }
    },
    async getInvoices ({ commit, state, rootGetters, dispatch }, { startingAfter = null, limit = 10, customerId = null }) {
      commit('setLoadingInvoices', true)
      try {
        const res = await apolloClient.query({
          query: GetInvoices,
          variables: {
            limit,
            ...(!!customerId && { customerId }),
            ...(!!startingAfter && { startingAfter })
          },
          fetchPolicy: 'no-cache'
        })

        if (startingAfter) {
          const newData = res.data.getInvoices?.invoices || []
          commit('setInvoices', [...state.invoices, ...newData])
        } else {
          commit('setInvoices', res.data.getInvoices?.invoices || [])
        }
        commit('setLoadingInvoices', false)
        return res.data.getInvoices
      } catch (e) {
        console.log(e)
        commit('setLoadingInvoices', false)
        commit('snackbar/showMessage', {
          content: 'Error, try again!',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }
    },
    async validateUsersSubscriptions ({ commit, state, rootGetters, dispatch }, { users }) {
      commit('setLoadingValidateUsersSubscriptions', true)
      try {
        const res = await apolloClient.mutate({
          mutation: ValidateUsersSubscriptions,
          variables: { users },
          fetchPolicy: 'no-cache'
        })

        commit('setLoadingValidateUsersSubscriptions', false)
        return res.data.validateUsersSubscriptions
      } catch (e) {
        console.log(e)
        commit('setLoadingValidateUsersSubscriptions', false)
        commit('snackbar/showMessage', {
          content: 'Error, try again!',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }
    }
  }
}
